import { Core } from "@faire/design-tokens";
import { setTimeoutIfPossible } from "@faire/next-utils/isomorphic/setTimeoutIfPossible";
import { IIntroProps } from "@faire/retailer-visitor-shared/views/brands/landing-page/intros";
import { Column, Row } from "@faire/web/slate/Layout";
import { Spacer, getSpacing } from "@faire/web/slate/spacing";
import { Typography } from "@faire/web/slate/Typography";
import { useViewport } from "@faire/web/ui/hooks/useViewport";
import { ResponsiveContentV2 } from "@faire/web/ui/ResponsiveContentV2";
import { trackBrandLeadsBrandsPageIntroSectionView } from "@faire/web-api--source/events/brandLeads/view/brandsPageIntroSection";
import padStart from "lodash/padStart";
import React, { useEffect, useState, type JSX } from "react";
import styled, { keyframes, css } from "styled-components";

const ANIMATION_MULTIPLE = 0.8;

export const BrandsLandingPageIntro: React.FC<IIntroProps> = (props) => {
  const [shouldAnimate, setShouldAnimate] = useState(true);

  useEffect(() => {
    setTimeoutIfPossible(
      () => {
        setShouldAnimate(false);
      },
      1000 * props.index * ANIMATION_MULTIPLE
    );
  }, [props.index]);

  return <Content {...props} shouldAnimate={shouldAnimate} />;
};

const Description = ({
  description,
}: {
  description: string | JSX.Element;
}) => {
  const { isMobile, isTabletAndAbove } = useViewport();

  return (
    <Typography
      variant={
        isTabletAndAbove ? "subheadingSansRegular" : "paragraphSansRegular"
      }
      color={Core.text.subdued}
      align={isMobile ? "center" : "left"}
    >
      {description}
    </Typography>
  );
};

const Header = ({ header }: { header: string }) => {
  const { isMobile } = useViewport();

  return (
    <Typography
      variant={{
        mobileAndAbove: "pageHeaderSerifRegular",
        tabletAndAbove: "displayMSerifRegular",
        desktopAndAbove: "displayLSerifRegular",
      }}
      align={isMobile ? "center" : "left"}
    >
      {header}
    </Typography>
  );
};

const MobileContent = ({
  index,
  header,
  description,
  shouldAnimate,
}: {
  index: number;
  header: string;
  description: string | JSX.Element;
  shouldAnimate: boolean;
}) => {
  return (
    <MobileValuePropsRow
      num={index}
      header={header}
      description={description}
      shouldAnimate={shouldAnimate}
    />
  );
};

const RichContent = ({ richContent }: { richContent: JSX.Element }) => (
  <Column>{richContent}</Column>
);

const DesktopCopy = ({
  header,
  description,
}: {
  header: string;
  description: string | JSX.Element;
}) => {
  return (
    <Column justify="center">
      <Row style={{ width: "384px" }}>
        <Header header={header} />
      </Row>
      <Spacer size="2x" />
      <Row style={{ width: "343px" }}>
        <Description description={description} />
      </Row>
    </Column>
  );
};

const RightImageContent = ({
  header,
  description,
  richContent,
}: {
  header: string;
  description: string | JSX.Element;
  richContent: JSX.Element;
}) => {
  const { isTablet } = useViewport();

  return (
    <>
      <DesktopCopy header={header} description={description} />
      <Spacer size={isTablet ? "5x" : "10x"} />
      <RichContent richContent={richContent} />
    </>
  );
};

const LeftImageContent = ({
  header,
  description,
  richContent,
}: {
  header: string;
  description: string | JSX.Element;
  richContent: JSX.Element;
}) => {
  const { isTablet } = useViewport();

  return (
    <>
      <RichContent richContent={richContent} />
      <Spacer size={isTablet ? "5x" : "10x"} />
      <DesktopCopy header={header} description={description} />
    </>
  );
};

export const Content = ({
  reverse,
  header,
  description,
  richContent,
  index,
  shouldAnimate,
  eventKey,
  startTracking,
}: {
  reverse?: boolean;
  header: string;
  description: string | JSX.Element;
  richContent: JSX.Element;
  index: number;
  shouldAnimate: boolean;
  eventKey: string;
  startTracking: boolean;
}) => {
  useEffect(() => {
    startTracking && trackBrandLeadsBrandsPageIntroSectionView(eventKey);
  }, [eventKey, startTracking]);

  return (
    <ResponsiveContentV2
      mobile={
        <Column
          justify="center"
          style={{ width: "100%", padding: getSpacing(0, "4x") }}
        >
          <MobileContent
            index={index}
            header={header}
            description={description}
            shouldAnimate={shouldAnimate}
          />
        </Column>
      }
      tabletAndAbove={
        <Row
          justify="space-between"
          align="center"
          style={{
            width: "100%",
          }}
        >
          {reverse ? (
            <LeftImageContent
              header={header}
              description={description}
              richContent={richContent}
            />
          ) : (
            <RightImageContent
              header={header}
              description={description}
              richContent={richContent}
            />
          )}
        </Row>
      }
    />
  );
};

const MobileValuePropsRow: React.FC<{
  num: number;
  header: string;
  description: string | JSX.Element;
  shouldAnimate: boolean;
}> = ({ num, header, description, shouldAnimate }) => (
  <AnimatedRow $shouldAnimate={shouldAnimate} $index={num - 1}>
    <Column>
      <Typography color={Core.text.subdued} variant="subheadingSansRegular">
        {padStart(num.toString(), 2, "0")}
      </Typography>
    </Column>
    <Spacer width="2x" />
    <Column>
      <Typography color={Core.text.default} variant="subheadingSansMedium">
        {header}
      </Typography>
      <Spacer height="1x" />
      <Typography color={Core.text.default} variant="paragraphSansRegular">
        {description}
      </Typography>
    </Column>
  </AnimatedRow>
);

const FadeIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;

const AnimatedRow = styled(Row)<{
  $index: number;
  $shouldAnimate: boolean;
}>`
  ${(props) =>
    props.$shouldAnimate
      ? css`
          opacity: 0;
          animation: ${FadeIn} ${ANIMATION_MULTIPLE}s linear forwards;
          animation-delay: ${ANIMATION_MULTIPLE * props.$index}s;
        `
      : ""}
`;
